import { IQueryClassDependency } from '@kurtosys/types/dependencies/IQueryClassDependency.js';
import { ITransformClassDependency } from '@kurtosys/types/dependencies/ITransformClassDependency.js';
import {
	IDates,
	IEntityRiskStatistics,
	IScenarioStatisticsResponse,
	IScenarioStatisticsResponsePeriod,
	TScenarioPeriodType,
} from '@kurtosys/types/portal/common/statistics/index.js';
import {
	AssetMatrix,
	Drawdown2,
	FundResult,
	IPortfolioStatisticsResult,
	PortfolioResult,
} from '@kurtosys/types/portal/common/statistics/response/result';
import { TPortfolioGetResponse, TScenarioGetResponse } from '@kurtosys/types/portal/index.js';
import { IExecutionOptions, IQueryContext, IQueryOptions } from '@kurtosys/types/query/index.js';
import { IQueryOptionsScenarioStatistic } from '@kurtosys/types/query/options/IQueryOptionsScenarioStatistic.js';
import { IQueryArrayOptions } from '@kurtosys/types/query/scenarioStatistics/IQueryArrayOptions.js';
import { IQueryAssetMatrixOptions } from '@kurtosys/types/query/scenarioStatistics/IQueryAssetMatrixOptions.js';
import { IQueryBenchmarkOptions } from '@kurtosys/types/query/scenarioStatistics/IQueryBenchmarkOptions.js';
import { IQueryDatesOptions } from '@kurtosys/types/query/scenarioStatistics/IQueryDatesOptions.js';
import { IQueryDrawdownOptions } from '@kurtosys/types/query/scenarioStatistics/IQueryDrawdownOptions.js';
import { IQueryEntityOptions } from '@kurtosys/types/query/scenarioStatistics/IQueryEntityOptions.js';
import { IQueryObjectArrayOptions } from '@kurtosys/types/query/scenarioStatistics/IQueryObjectArrayOptions.js';
import { IQueryPeriodsOptions } from '@kurtosys/types/query/scenarioStatistics/IQueryPeriodsOptions.js';
import { IQueryPortfolioBaseOptions } from '@kurtosys/types/query/scenarioStatistics/IQueryPortfolioBaseOptions.js';
import { IQueryPortfolioOptions } from '@kurtosys/types/query/scenarioStatistics/IQueryPortfolioOptions.js';
import { IQueryPortfoliosOptions } from '@kurtosys/types/query/scenarioStatistics/IQueryPortfoliosOptions.js';
import { IQueryScenarioStatisticOptions } from '@kurtosys/types/query/scenarioStatistics/IQueryScenarioStatisticOptions.js';
import { IQueryStatisticsCollectionOptions } from '@kurtosys/types/query/scenarioStatistics/IQueryStatisticsCollectionOptions.js';
import { IQueryStatisticsValueTransform } from '@kurtosys/types/query/scenarioStatistics/IQueryStatisticsValueTransform.js';
import { ScenarioStatisticShortcut } from '@kurtosys/types/query/scenarioStatistics/ScenarioStatisticShortcut.js';
import { ScenarioStatisticTarget } from '@kurtosys/types/query/scenarioStatistics/ScenarioStatisticTarget.js';
import { IQueryShortcutOptions } from '@kurtosys/types/query/scenarioStatistics/shortcuts/IQueryShortcutOptions.js';
import { ShortcutType } from '@kurtosys/types/query/scenarioStatistics/shortcuts/ShortcutType.js';
import { ITransformOptions } from '@kurtosys/types/query/transforms/ITransformOptions.js';

import { commonUtils, helpers } from '../../common/index.js';
import { getQueryClass } from '../queryUtils/getQueryClass.js';
import { queryHelper } from '../queryUtils/index.js';
import * as shortcuts from '../scenarioStatistics/shortcuts/index.js';
import { Transform } from '../transform/Transform.js';

import { BaseQuery } from './base/BaseQuery.js';

type TShortcuts = {
	[key in ScenarioStatisticShortcut]: shortcuts.ShortcutBase;
};

export class ScenarioStatisticQuery extends BaseQuery<IQueryOptionsScenarioStatistic> {
	constructor(
		public dependencies: IQueryClassDependency & ITransformClassDependency,
		public queryOptions: IQueryOptions,
		public translationHelper?: helpers.TranslationHelper,
	) {
		super(dependencies, queryOptions, translationHelper);
	}

	getOptions(): IQueryOptionsScenarioStatistic {
		return (
			this.queryOptions &&
			((this.queryOptions.options && this.queryOptions.options.scenarioStatistic) ||
				this.queryOptions.scenarioStatisticOptions ||
				{})
		);
	}
	getRawValue<T = any>(executionOptions: IExecutionOptions): T {
		this.executionOptions = executionOptions;
		const response: T = this.getValueForRoot(this.statistics, this.scenarioStatisticOptions);
		return response;
	}

	private getValueForRoot(
		scenarioStatistics: IScenarioStatisticsResponse | undefined,
		rootOptions: IQueryScenarioStatisticOptions,
	): any {
		let response: any = scenarioStatistics;
		if (rootOptions && scenarioStatistics) {
			const { key, options = {}, shortcut, transforms } = rootOptions;
			if (shortcut) {
				response = this.getValueForShortcut(scenarioStatistics, shortcut);
			} else {
				switch (key) {
					case 'periods':
						response = this.getValueForPeriods(scenarioStatistics.periods, options[key]) as any;
						break;
					default:
						response = scenarioStatistics[key];
						response = this.applyTransforms(response, transforms, this.queryOptions, this.executionOptions);
						break;
				}
			}
		}
		return response;
	}

	private getValueForShortcut(
		scenarioStatistics: IScenarioStatisticsResponse,
		shortcutOptions: IQueryShortcutOptions,
	): any {
		let response: any = undefined;
		if (shortcutOptions) {
			const { key } = shortcutOptions;
			const shortcut = this.shortcuts[key];
			if (shortcut) {
				const rootOptions = shortcut.getRootOptions(shortcutOptions);
				switch (shortcut.type) {
					case ShortcutType.directResponse:
						response = shortcut.getDirectResponse(shortcutOptions, scenarioStatistics);
						break;
					case ShortcutType.rootOptions:
						response = this.getValueForRoot(scenarioStatistics, rootOptions);
						break;
				}
			}
		}
		return response;
	}

	public shortcuts: TShortcuts = {
		entityRisksTable: new shortcuts.EntityRisksTableShortcut(this),
		portfoliosMptStatistics: new shortcuts.PortfoliosMptStatisticsShortcut(this),
		portfolios: new shortcuts.PortfoliosShortcut(this),
		fund: new shortcuts.FundShortcut(this),
		funds: new shortcuts.FundsShortcut(this),
		portfolio: new shortcuts.PortfolioShortcut(this),
		portfolioCompositeFund: new shortcuts.PortfolioCompositeFundShortcut(this),
		portfolioCorrelation: new shortcuts.PortfolioCorrelationShortcut(this),
		portfoliosBarChartData: new shortcuts.PortfoliosBarChartDataShortcut(this),
		portfoliosScatterChartData: new shortcuts.PortfoliosScatterChartDataShortcut(this),
	};

	private getValueForPeriods(periods: IScenarioStatisticsResponsePeriod[], periodOptions: IQueryPeriodsOptions): any {
		let response: any = periods;
		if (periodOptions) {
			const { target = ScenarioStatisticTarget.single } = periodOptions;
			if (target === ScenarioStatisticTarget.single) {
				const singlePeriodType = this.getSinglePeriodType(periodOptions);
				const period = this.getPeriod(singlePeriodType);
				response = this.getValueForPeriod(period, periodOptions);
			} else if (target === ScenarioStatisticTarget.collection) {
				response = this.getCollectionResponse(
					periods,
					(item): any => this.getValueForPeriod(item, periodOptions),
					(item): any => item.periodType.toString(),
					periodOptions.collectionOptions,
				);
			}
		}
		return response;
	}

	public applyValueTransform(value: any, valueTransform: IQueryStatisticsValueTransform | undefined): any {
		let response = value;
		if (valueTransform) {
			const { prefix, suffix, staticValue, transforms } = valueTransform;
			if (!commonUtils.isNullOrUndefined(staticValue)) {
				response = staticValue;
			}
			if (transforms) {
				response = this.applyTransforms(response, transforms, this.queryOptions, this.executionOptions);
			}
			if (!commonUtils.isNullOrEmpty(prefix)) {
				response = `${prefix}${response}`;
			}
			if (!commonUtils.isNullOrEmpty(suffix)) {
				response = `${response}${suffix}`;
			}
		}
		return response;
	}

	private applyTransforms(
		value: any,
		transforms: ITransformOptions[],
		queryOptions: IQueryOptions,
		executionOptions: IExecutionOptions,
	): any {
		if (transforms) {
			for (const transformOptions of transforms) {
				const transform = new Transform(this.dependencies, transformOptions, this.translationHelper);
				value = transform.execute(value, queryOptions, executionOptions);
			}
		}
		return value;
	}

	private getCollectionResponse<T>(
		collection: T[],
		getItem: (item: T) => any,
		getId: (item: T) => string,
		options: IQueryStatisticsCollectionOptions = {},
	): Record<string, any> {
		const { pivotToObject, injectId } = options;
		if (pivotToObject && pivotToObject.enabled) {
			const { transform = {} } = pivotToObject;
			const { key: keyTransform, value: valueTransform } = transform;
			return collection.reduce((acc, item): Record<string, any> => {
				const itemResponse = getItem(item);
				const rawId = getId(item);
				if (!commonUtils.isNullOrUndefined(rawId)) {
					const id = this.applyValueTransform(rawId, keyTransform);
					acc[id] = this.applyValueTransform(itemResponse, valueTransform);
				}
				return acc;
			}, {});
		}
		if (injectId && injectId.enabled) {
			const { transform = {} } = injectId;
			const { key: keyTransform, value: valueTransform } = transform;
			return collection.map((item): any => {
				const itemResponse = getItem(item);
				const rawId = getId(item);
				if (!commonUtils.isNullOrUndefined(rawId)) {
					const id = this.applyValueTransform(rawId, valueTransform);
					const key = this.applyValueTransform('id', keyTransform);
					if (Array.isArray(itemResponse)) {
						return { [key]: id, values: itemResponse };
					}
					if (commonUtils.isObject(itemResponse)) {
						return { [key]: id, ...itemResponse };
					}
				}
				return itemResponse;
			});
		}
		return collection.map((item): any => {
			const itemResponse = getItem(item);
			return itemResponse;
		});
	}

	private getValueForPeriod(period: IScenarioStatisticsResponsePeriod, periodOptions: IQueryPeriodsOptions): any {
		let response: any = period;
		if (!period) {
			return undefined;
		}
		const { key, options = {}, excludeBenchmarkFromPortfolios, transforms } = periodOptions;
		if (key) {
			const portfolios = [...(period.portfolios || [])];
			switch (key) {
				case 'dates':
					response = this.getValueForDates(period[key], options[key]);
					break;
				case 'benchmark':
					response = this.getValueForBenchmark(period[key], options[key]);
					break;
				case 'portfolios':
					if (!excludeBenchmarkFromPortfolios && period.benchmark) {
						portfolios.push(period.benchmark);
					}
					response = this.getValueForPortfolios(portfolios, options[key]);
					break;
				case 'entityRisks':
					response = this.getValueForEntityRisks(period[key]);
					break;
				default:
					response = period[key];
					break;
			}
		}
		response = this.applyTransforms(response, transforms, this.queryOptions, this.executionOptions);
		return response;
	}

	private getValueForDates(dates: IDates, datesOptions: IQueryDatesOptions): any {
		let response: any = undefined;
		if (!dates) {
			return undefined;
		}
		const { key, options = {}, transforms } = datesOptions;
		switch (key) {
			case 'commonDates':
			case 'allDates':
				response = this.getValueForArray(dates[key], options[key]) as any;
				break;
			default:
				response = dates[key];
				break;
		}
		response = this.applyTransforms(response, transforms, this.queryOptions, this.executionOptions);
		return response;
	}

	private getValueForEntityRisks(entityRisks: IEntityRiskStatistics[]): any {
		const response: any = entityRisks;
		if (!entityRisks) {
			return undefined;
		}
		return response;
	}

	private getValueForBenchmark(benchmark: IPortfolioStatisticsResult, benchmarkOptions: IQueryBenchmarkOptions): any {
		return this.getValueForPortfolioBase(benchmark, benchmarkOptions);
	}

	public getValueForPortfolioBase(
		portfolioResult: IPortfolioStatisticsResult,
		portfolioBaseOptions: IQueryPortfolioBaseOptions,
	): any {
		let response: any = undefined;
		if (!portfolioResult) {
			return undefined;
		}
		const { key, options = {}, transforms } = portfolioBaseOptions;
		switch (key) {
			case 'funds':
				response = this.getValueForEntities(portfolioResult[key], options[key]);
				break;
			case 'portfolio':
				response = this.getValueForPortfolio(portfolioResult[key], options[key]) as any;
				break;
			default:
				response = portfolioResult[key];
				break;
		}
		response = this.applyTransforms(response, transforms, this.queryOptions, this.executionOptions);
		return response;
	}

	private getValueForPortfolios(
		portfolios: IPortfolioStatisticsResult[],
		portfolioOptions: IQueryPortfoliosOptions,
	): any {
		let response: any = portfolios;

		const { target = ScenarioStatisticTarget.single } = portfolioOptions || {};
		if (target === ScenarioStatisticTarget.single) {
			const singlePortfolioId = this.getSinglePortfolioId(portfolioOptions);
			const portfolio = this.getPortfolio(portfolios, singlePortfolioId);
			response = this.getValueForPortfolioBase(portfolio, portfolioOptions);
		} else if (target === ScenarioStatisticTarget.collection) {
			response = this.getCollectionResponse(
				portfolios,
				(item): any => this.getValueForPortfolioBase(item, portfolioOptions),
				(item): string => item.id && item.id.toString(),
				portfolioOptions.collectionOptions,
			);
		}

		return response;
	}

	public getValueForPortfolio(portfolio: PortfolioResult, portfolioOptions: IQueryPortfolioOptions): any {
		let response: any = undefined;
		const { key, options = {}, transforms } = portfolioOptions || {};
		switch (key) {
			case 'MCTR':
			case 'absCTR':
			case 'correlation':
			case 'covariance':
			case 'RSquared':
			case 'excessReturnsCorrelation':
				response = this.getValueForAssetMatrix(portfolio[key], options[key]) as any;
				break;
			case 'trailingReturns':
			case 'forecast':
				response = this.getValueForObjectArray(portfolio[key], options[key]) as any;
				break;
			case 'error':
				response = this.getValueForArray(portfolio[key], options[key]) as any;
				break;
			case 'compositeFund':
				response = this.getValueForEntity(portfolio[key], options[key]) as any;
				break;
			default:
				response = portfolio[key];
				break;
		}
		response = this.applyTransforms(response, transforms, this.queryOptions, this.executionOptions);
		return response;
	}

	private getValueForDrawdown(drawdown: Drawdown2, drawdownOptions: IQueryDrawdownOptions): any {
		let response: any = undefined;
		const { key, options = {}, transforms } = drawdownOptions || {};
		switch (key) {
			case 'drawdownSeries':
				response = this.getValueForObjectArray(drawdown[key], options[key]) as any;
				break;
			default:
				response = drawdown[key];
				break;
		}
		response = this.applyTransforms(response, transforms, this.queryOptions, this.executionOptions);
		return response;
	}

	private getValueForEntities(entities: FundResult[], entityOptions: IQueryEntityOptions): any {
		let response: any = entities;

		const { target = ScenarioStatisticTarget.single } = entityOptions || {};
		if (target === ScenarioStatisticTarget.single) {
			const singlePortfolioEntityId = this.getSinglePortfolioEntityId(entityOptions);
			const entity = this.getFund(entities, singlePortfolioEntityId);
			response = this.getValueForEntity(entity, entityOptions);
		} else if (target === ScenarioStatisticTarget.collection) {
			response = this.getCollectionResponse(
				entities,
				(item): any => this.getValueForEntity(item, entityOptions),
				(item): string => item.name && item.name.toString(),
				entityOptions.collectionOptions,
			);
		}
		return response;
	}

	public getValueForEntity(entity: FundResult, entityOptions: IQueryEntityOptions): any {
		let response: any = entity;
		if (entityOptions && entity) {
			const { key, options = {}, transforms } = entityOptions;
			if (key) {
				switch (key) {
					case 'bestPeriod':
					case 'worstPeriod':
					case 'calendarYearReturns':
					case 'excessReturns':
					case 'excessReturnsOverRF':
					case 'trailingYearReturns':
					case 'trailingQuarterReturnsCumulative':
					case 'trailingQuarterReturns':
					case 'trailingVolatilityPop':
					case 'trailingVolatilitySam':
					case 'longestPosPeriod':
					case 'longestNegPeriod':
					case 'riskFreeReturns':
					case 'rollingAverage':
					case 'seriesSplice':
						response = this.getValueForObjectArray(entity[key], options[key]) as any;
						break;
					case 'drawdown2':
					case 'drawdown2Benchmark':
					case 'drawdown2RF':
						response = this.getValueForDrawdown(entity[key], options[key]);
						break;
					case 'error':
						response = this.getValueForArray(entity[key], options[key]) as any;
						break;
					default:
						response = entity[key];
						break;
				}
			}
			response = this.applyTransforms(response, transforms, this.queryOptions, this.executionOptions);
		}
		return response;
	}

	private getValueForAssetMatrix(assetMatrix: AssetMatrix, options: IQueryAssetMatrixOptions): any {
		let response: any = assetMatrix;
		if (assetMatrix && options) {
			const { matrix, portfolioNames } = assetMatrix;
			if (matrix && portfolioNames && matrix.length === portfolioNames.length) {
				if (options.pivotToTableData) {
					const { excludeAboveTheFoldValues = false } = options;
					const rows = [];
					const columns = [];
					for (let outerIndex = 0; outerIndex < portfolioNames.length; outerIndex++) {
						const portfolioName = portfolioNames[outerIndex];
						const matrixRow = matrix[outerIndex];
						const row: any = {
							index: outerIndex + 1,
							id: portfolioName,
						};
						for (let innerIndex = 0; innerIndex < portfolioNames.length; innerIndex++) {
							if (excludeAboveTheFoldValues && innerIndex > outerIndex) {
								break;
							}
							const columnKey = portfolioNames[innerIndex];
							row[columnKey] =
								matrixRow.length === portfolioNames.length ? matrixRow[innerIndex] : undefined;
							if (columns.length < innerIndex + 1) {
								columns.push({
									key: columnKey,
									header: `${innerIndex + 1}`,
								});
							}
						}
						rows.push(row);
					}
					response = {
						rows,
						columns,
					};
				} else if (options.matrix || options.percentMatrix) {
					let target = options.matrix;
					let targetKey = 'matrix';
					if (options.percentMatrix) {
						target = options.percentMatrix;
						targetKey = 'percentMatrix';
					}
					const { subIndex = 0 } = target;
					const portfolioNameQuery = new this.dependencies.queryClass(
						target.portfolioName,
						this.translationHelper,
					);
					const targetPortfolioName = portfolioNameQuery.execute(this.executionOptions);
					const index = assetMatrix.portfolioNames.findIndex(
						(portfolioName): boolean => portfolioName === targetPortfolioName,
					);
					if (
						assetMatrix[targetKey] &&
						assetMatrix[targetKey].length > index &&
						assetMatrix[targetKey][index] &&
						assetMatrix[targetKey][index].length > 0
					) {
						response = assetMatrix[targetKey][index][subIndex];
					} else {
						response = 0;
					}
				}
			}
		}

		return response;
	}

	private getValueForObjectArray(arr: object[], options: IQueryObjectArrayOptions<any>): any {
		let response: any = arr;
		if (options && Array.isArray(arr)) {
			const { index, startIndex } = options;
			if (!commonUtils.isNullOrUndefined(index) && arr.length > index) {
				response = arr[index];
				if (!commonUtils.isNullOrUndefined(options.key) && typeof response === 'object') {
					response = response[options.key];
				}
			} else if (!commonUtils.isNullOrUndefined(startIndex)) {
				if (startIndex >= 0) {
					response = arr.slice(startIndex);
				}
				if (startIndex < 0) {
					response = arr.slice(Math.max(arr.length + startIndex, 0));
				}
			}
		}

		return response;
	}

	private getValueForArray(arr: any[], options: IQueryArrayOptions): any {
		let response: any = arr;
		if (
			options &&
			Array.isArray(arr) &&
			!commonUtils.isNullOrUndefined(options.index) &&
			arr.length > options.index
		) {
			response = arr[options.index];
		}
		return response;
	}

	get scenarioStatisticOptions(): IQueryScenarioStatisticOptions {
		return this.options;
	}

	public executionOptions: IExecutionOptions | undefined;

	get inputs(): object {
		return (this.executionOptions && this.executionOptions.inputs) || {};
	}

	private rawPeriodType: TScenarioPeriodType | undefined;
	getSinglePeriodType(periodOptions: IQueryPeriodsOptions = {}): TScenarioPeriodType | undefined {
		if (!this.rawPeriodType && periodOptions) {
			if (periodOptions.type) {
				this.rawPeriodType = periodOptions.type;
			} else if (this.inputs['periodType']) {
				const inputPeriodType = this.inputs['periodType'];
				const parsedValue = parseInt(inputPeriodType, 10);
				this.rawPeriodType = (Number.isNaN(parsedValue) ? 'all' : parsedValue) as TScenarioPeriodType;
			}
		}
		return this.rawPeriodType;
	}

	private rawPortfolioId: string | undefined;
	getSinglePortfolioId(options: IQueryPortfoliosOptions = {}): string | undefined {
		if (!this.rawPortfolioId && options) {
			if (options.id) {
				this.rawPortfolioId = options.id.toString();
			} else if (options.idQuery) {
				const portfolioIdQuery = getQueryClass(this.dependencies, options.idQuery, this.translationHelper)
				if (portfolioIdQuery) {
					this.rawPortfolioId = portfolioIdQuery.execute(this.executionOptions);
				}
			} else if (this.inputs['portfolioId']) {
				this.rawPortfolioId = this.inputs['portfolioId'].toString();
			}
		}
		return this.rawPortfolioId;
	}

	private rawPortfolioEntityId: string | undefined;
	getSinglePortfolioEntityId(options: IQueryEntityOptions = {}): string | undefined {
		if (!this.rawPortfolioEntityId && options) {
			if (options.id) {
				this.rawPortfolioEntityId = options.id.toString();
			} else if (options.idQuery) {
				const portfolioEntityIdQuery = new this.dependencies.queryClass(
					options.idQuery,
					this.translationHelper,
				);
				this.rawPortfolioEntityId = portfolioEntityIdQuery.execute(this.executionOptions);
			} else if (this.inputs['portfolioEntityId']) {
				this.rawPortfolioEntityId = this.inputs['portfolioEntityId'].toString();
			}
		}
		return this.rawPortfolioEntityId;
	}

	getPeriod(periodType: TScenarioPeriodType | undefined): IScenarioStatisticsResponsePeriod | undefined {
		if (this.statistics && periodType && this.statistics.periods) {
			return this.statistics.periods.find((period): boolean => period.periodType === periodType);
		}
	}

	getPortfolioStatisticResult(
		period: IScenarioStatisticsResponsePeriod,
		portfolioId: string | undefined,
	): IPortfolioStatisticsResult | undefined {
		let response: IPortfolioStatisticsResult | undefined;
		if (period && portfolioId) {
			response = period.portfolios.find((portfolio): boolean => this.idEquals(portfolio.id, portfolioId));
			if (!response && this.idEquals(period.benchmark.id, portfolioId)) {
				response = period.benchmark;
			}
		}
		return response;
	}

	getFund(funds: FundResult[] | undefined, portfolioEntityId: string | undefined): FundResult | undefined {
		if (funds && portfolioEntityId) {
			return funds.find((fund): boolean => this.idEquals(fund.name, portfolioEntityId));
		}
		return undefined;
	}

	getPortfolio(
		portfolios: IPortfolioStatisticsResult[] | undefined,
		portfolioId: string | undefined,
	): PortfolioResult | undefined {
		if (portfolios && portfolioId) {
			const portfolio = portfolios.find((portfolio): boolean => this.idEquals(portfolio.id, portfolioId));
			return portfolio;
		}
		return undefined;
	}

	private getDates(period: IScenarioStatisticsResponsePeriod | undefined): IDates {
		return period && period.dates;
	}

	get context(): IQueryContext {
		return queryHelper.getContext(this.executionOptions, this.queryOptions);
	}

	get statistics(): IScenarioStatisticsResponse {
		return this.context && this.context.scenarioStatistics;
	}

	get scenario(): TScenarioGetResponse {
		return this.context && this.context.scenario;
	}

	get portfolio(): TPortfolioGetResponse {
		return this.context && this.context.portfolio;
	}

	idEquals = (idA: number | string, idB: number | string): boolean => {
		return (
			idA === idB ||
			(!commonUtils.isNullOrUndefined(idA) &&
				!commonUtils.isNullOrUndefined(idB) &&
				idA.toString() === idB.toString())
		);
	};
}
